import { Injectable } from '@angular/core';
import {BehaviorSubject, from, Subject} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CacheService } from 'ionic-cache';
import { API_URL, PORT, PREFIX } from '../../environments/environment';
import { NavController } from '@ionic/angular';
import { CentralAuthenticationService } from './central-authentication.service';
import { mergeMap, share } from 'rxjs/operators';
import { Refresh } from './api.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  public $dynamicMenu = new BehaviorSubject([]);
  // private url = '';
  private authSuffix = '/api/authenticate';
  private tokenSuffix = '-token';
  private tenantCacheUrl = 'current-tenant';
  private groupKey = 'tenant-auth';


  constructor(private http: HttpClient,
              private cache: CacheService,
              private navController: NavController,
              private centralAuthService: CentralAuthenticationService,) {

  }

  // public updateToken(token) {
  //   return this.cache.saveItem(this.url, token);
  // }

  public isAuthenticated() {
    const result = new Subject<boolean>();
    this.getTenantToken().then(
      token => {
        this.centralAuthService.$currentUserEmail.next(token.email);
        if (token && token.token_type && token.access_token) {
          const headers = {
            authorization: token.token_type + ' ' + token.access_token,
          };
          const request = this.http.get(
            PREFIX + token.domains[0].domain + PORT + this.authSuffix,
            { headers }
          );
          const response = this.cache.loadFromDelayedObservable(
            token.domains[0].domain + '-authenticate-' + token.email,
            request,
            this.groupKey,
            60,
            'all'
          );

          response.subscribe(
            res => {
              this.loadMenu().subscribe(() => { });
              result.next(true);
              result.complete();
            },
            error => {
              result.next(false);
              result.complete();
              this.removeToken();
            }
          );
        } else {
          result.next(false);
          result.complete();
        }
      },
      error => {
        result.next(false);
        result.complete();
      }
    );
    return result.asObservable();
  }

  async getTenant() {
    return await this.cache.getItem(this.tenantCacheUrl).catch(
      () => {

      }
    );
  }

  public async removeToken() {
    return await this.getTenant().then(
      tenant => {
        if (tenant) {
          return this.cache.removeItem(tenant.domains[0].domain + this.tokenSuffix);
        } else {
          return this.cache.clearGroup(this.groupKey);
        }

      });
  }

  public storeToken(token) {
    return this.cache.saveItem(token.domains[0].domain + this.tokenSuffix, token, this.groupKey);
  }

  async getTenantToken() {
    return await this.getTenant().then(
      async tenant => {
        if (tenant) {
          return await this.getToken(tenant);
        } else {
          return false;
        }
      },
      error => {
        console.log(error);
        return error;
      }
    );
  }

  public tryTokenForward(tenant) {
    const call = from(this.centralAuthService.getToken()).pipe(mergeMap(token => {
      const headers = new HttpHeaders({
        authorization: token.token_type + ' ' + token.access_token,
      });
      return this.http.get(API_URL + 'forward/' + tenant.id, { headers }).pipe(share());
    })).pipe(share());
    call.subscribe(token => {
      this.storeToken(token);
    },
      error => { console.log(error); });
    return call;
  }

  public login(email, password) {

    this.getTenant().then(
      tenant => {
        if (tenant) {
          const data = {
            email,
            password
          };
          const request = this.http.post<any>(PREFIX + tenant.domains[0].domain + PORT + '/api/login', data);

            request.subscribe(
            token => {
              if (token && token.tenant) {
                this.storeToken(token).then(
                  () => {
                    this.navController.navigateForward(['home']);
                  });

              }
            }
          );
        }
      }
    );

  }

  public logout() {
    this.getTenantToken().then(
      token => {
        const headers = new HttpHeaders({
          authorization: token.token_type + ' ' + token.access_token,
        });
        const request = this.http.get(PREFIX + token.domains[0].domain + PORT + '/api/logout', { headers });
        request.subscribe(
          res => {
            this.removeToken().then(
              () => {
                this.centralAuthService.removeTenant().then(
                  () => {
                    this.$dynamicMenu.next([]);
                    this.navController.navigateBack(['launch']);
                  }
                );
              }
            );
          },
          error => {
            console.log(error);
            this.cache.clearGroup(this.groupKey);
          }
        );
        }
    );

  }


  private loadMenu(refresh: Refresh = 'expired') {
    const call = from(this.getTenantToken()).pipe(mergeMap(token => {
      const headers = new HttpHeaders({
        authorization: token.token_type + ' ' + token.access_token,
      });
      const url = PREFIX + token.domains[0].domain + PORT + '/api/menu';
      const request = this.http.get(url, { headers });
      return this.cache.loadFromDelayedObservable(
        token.domains[0].domain + '-menu', request,
        this.groupKey,
        60 * 60 * 24,
        refresh
      ).pipe(share());
    })).pipe(share());
    call.subscribe(res => {
      if (res instanceof Array) {
        const equal = (res as Array<any>).every(($menu, i) => $menu.url === this.$dynamicMenu.value[i]?.url);
        if (!equal) {
          this.$dynamicMenu.next(res as Array<any>);
        }
      }
    });
    return call;
  }

  private async getToken(tenant) {
    if (tenant) {
      return await this.cache.getItem(tenant?.domains[0].domain + this.tokenSuffix).catch(
        () => {

        }
      );
    } else {
      throw new Error('Could not find a tenant in memory');
    }

  }
}
